import { Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PinnedItem, SideNavigationItem } from '@vendasta/atlas';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { BusinessNavDataService } from '../data.service';
import { SalesInfoModalComponent } from '../modals/sales-info/sales-info.modal.component';
import { PinsService } from '../pins.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'atlas-business-sidebar',
  templateUrl: './business-sidebar.component.html',
  styleUrls: ['./business-sidebar.component.scss'],
})
export class BusinessSidebarComponent {
  public businessCenterName$: Observable<string>;
  public partnerName$: Observable<string>;
  public logoUrl$: Observable<string>;
  public coBrandingUrl$: Observable<string>;
  public contactMessage$: Observable<string>;
  public salespersonPictureUrl$: Observable<string>;
  public navigationItems$ = combineLatest([this.apiService.navigationItems$, this.pinsService.pins$]).pipe(
    map(([items, pins]) => removeUnpinnedItems(items, pins)),
  );
  public mobile$: Observable<boolean>;
  public businessAppBranding$: Observable<boolean>;
  logoHeight$$: ReplaySubject<number> = new ReplaySubject<number>(1);
  logoHeight$: Observable<number>;
  @ViewChild('injectionSpace', { static: true }) injectionSpace: ElementRef;

  constructor(
    private apiService: BusinessNavDataService,
    private sidebarService: SidebarService,
    private translateService: TranslateService,
    private pinsService: PinsService,
    private dialog: MatDialog,
  ) {
    this.logoHeight$ = this.logoHeight$$.asObservable().pipe(
      tap((height: number) => {
        this.rememberLastLogoHeight(height);
      }),
      startWith(this.getLastLogoHeight()),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.coBrandingUrl$ = this.apiService.cobrandingLogoURL$.pipe(map((url) => url || null));
    this.mobile$ = this.sidebarService.mobile$;
    this.businessCenterName$ = this.apiService.businessCenterName$;
    this.partnerName$ = this.apiService.partnerName$;
    this.logoUrl$ = this.apiService.marketLogoUrl$;
    this.salespersonPictureUrl$ = this.apiService.salespersonPictureUrl$;
    this.businessAppBranding$ = this.apiService.businessAppBranding$;

    this.contactMessage$ = this.apiService.salespersonName$.pipe(
      startWith('Us'),
      switchMap((name: string) => {
        if (name === 'Us') {
          return this.translateService.stream('COMMON.ACTION_LABELS.CONTACT_US');
        }
        return this.translateService.stream('COMMON.CONTACT_SALESPERSON', { salesPersonName: name });
      }),
    );
  }

  logoLoaded(logo: { clientHeight: number }): void {
    window.setTimeout(() => {
      this.logoHeight$$.next(Math.min(logo.clientHeight, 120));
    }, 0);
  }

  openAssistanceModal(): void {
    this.dialog.open(SalesInfoModalComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
    });
  }

  private rememberLastLogoHeight(height: number): void {
    localStorage.setItem('business-sidenav-logo-height', '' + height);
  }

  private getLastLogoHeight(): number {
    const t = localStorage.getItem('business-sidenav-logo-height');
    if (!t) {
      return 80;
    }
    return +t;
  }
}

export function removeUnpinnedItems(items: SideNavigationItem[], pins: PinnedItem[]): SideNavigationItem[] {
  if (!pins?.length) return items;

  return items.filter((item) => {
    if (!(item.sideNavigationLink || item.sideNavigationContainer)) return true;
    const relevantItem = item.sideNavigationLink || item.sideNavigationContainer;
    return !relevantItem.pinnable || pins.find((pin) => pin.navigationId === relevantItem.navigationId);
  });
}
